import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Home, 
  Sparkle, 
  Compass, 
  Star, 
  User,
  Menu,
  X,
  FileText,
  Calculator,
  Settings,
  HelpCircle,
  Crown,
  LogOut,
  Bell,
  ChevronRight,
  Download,
  LayoutDashboard,
  LogIn
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Public, anyone-can-buy report landing pages. We strip the app shell on
// these routes so the page renders cleanly on phones — no dashboard strip
// at the top and no bottom tab bar hiding the "Pay Now" CTA.
const MARKETING_LANDING_ROUTES = new Set([
  "/mahakundli",
  "/lalkitab",
  "/bnn",
  "/raj-yog",
  "/life-report",
  "/astro-report",
  "/vastu-report",
  "/numerology-report",
  "/astro-vastu-report",
  "/palmistry-report",
  "/face-reading-report",
  "/consultation",
]);

// Bottom Navigation Items
const bottomNavItems = [
  { id: "home", label: "Home", icon: Home, path: "/" },
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { id: "vastu", label: "Vastu", icon: Compass, path: "/editor" },
  { id: "ai", label: "AI Guru", icon: Sparkle, path: "/pinnacle-guru" },
  { id: "profile", label: "Profile", icon: User, path: "/profile" },
];

// Side Menu Items
const menuItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { id: "client-crm", label: "Occult Manager", icon: User, path: "/client-crm", premium: true },
  { id: "numerology", label: "Numerology Tools", icon: Calculator, path: "/numerology" },
  { id: "vastu-editor", label: "Vastu Editor", icon: Compass, path: "/editor" },
  { id: "projects", label: "My Projects", icon: FileText, path: "/projects" },
  { id: "premium", label: "Go Premium", icon: Crown, path: "/profile", highlight: true },
  { id: "support", label: "Help & Support", icon: HelpCircle, path: "/support" },
  { id: "settings", label: "Settings", icon: Settings, path: "/profile" },
];

export function MobileAppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Check if running as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsPWA(isStandalone);

    // Load user
    const userData = localStorage.getItem('vastu_user') || localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Failed to parse user');
      }
    }

    // Check notification permission
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Print-native mode: when a report page is being rendered for server-side
  // PDF capture, drop the entire app shell so headers, nav, sidebar, toasts
  // never appear in the final PDF. (Placed after hooks to obey rules-of-hooks.)
  const isPrintNative = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('print_native') === '1';
  if (isPrintNative) {
    return <>{children}</>;
  }

  // Marketing landing pages — public, anyone-can-buy report sales pages.
  // These are shared via WhatsApp / social ads, so we strip the app shell
  // (top dashboard strip + bottom tab bar) to keep the page clean on phones
  // and ensure the "Pay Now" CTA is fully visible instead of being hidden
  // behind the fixed bottom navigation.
  if (MARKETING_LANDING_ROUTES.has(location.pathname)) {
    return <>{children}</>;
  }

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('vastu_user');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setIsMenuOpen(false);
    window.dispatchEvent(new Event('userLogout'));
    navigate('/');
    toast.success('Logged out successfully');
  };

  const enableNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      toast.success('Notifications enabled!');
      
      // Subscribe to push notifications
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(
              'BNJADDLqTG4H6s5XdARsGX8mCIdlIjm-gfls-5K9hUQFzLl73xcw9772oVD1Dwyg8CxpsD1OrboUzW5i-gzCu5w'
            )
          });
          
          // Save subscription to backend
          if (user?.id) {
            await fetch(`${API}/api/push/subscribe`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: user.id,
                subscription: subscription.toJSON()
              })
            });
          }
        } catch (err) {
          console.error('Push subscription error:', err);
        }
      }
    } else {
      toast.error('Notification permission denied');
    }
  };

  // Get active tab
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/dashboard' || path.startsWith('/dashboard')) return 'dashboard';
    if (path.includes('pinnacle-guru') || path.includes('astro-guru')) return 'ai';
    if (path.includes('vastu') || path.includes('editor')) return 'vastu';
    if (path.includes('astro')) return 'astro';
    if (path.includes('profile')) return 'profile';
    return '';
  };

  const activeTab = getActiveTab();

  // Mobile chrome rules (declutter the top for ad traffic):
  //  • Pages that render their own top header get the shell's mobile header
  //    suppressed so we never stack two headers on top of each other.
  //  • Remedies product pages also drop the bottom nav so the sticky
  //    "Add to Cart" bar is unobstructed and buyers stay focused.
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const isRemedyProduct = /^\/remedies\/.+/.test(path);
  const isRemedyGrid = path === "/remedies";
  const NATIVE_HEADER_ROUTES = ["/", "/dashboard"];
  const hideMobileHeader = NATIVE_HEADER_ROUTES.includes(path) || isRemedyGrid || isRemedyProduct;
  const hideBottomNav = isRemedyProduct;

  // Don't show mobile shell on certain pages
  const hideShellPages = ['/admin', '/login', '/signup', '/forgot-password', '/install', '/get-app', '/portal/', '/print/'];
  const isEmbed = new URLSearchParams(location.search).get('embed') === '1';
  const shouldHideShell = isEmbed || hideShellPages.some(p => location.pathname.startsWith(p));
  
  if (shouldHideShell) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Mobile Header - hidden on pages that render their own top header */}
      {!hideMobileHeader && (
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200 safe-area-top">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Hamburger Menu */}
          <button 
            onClick={() => setIsMenuOpen(true)}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors"
            data-testid="hamburger-menu-btn"
          >
            <Menu size={22} className="text-slate-700" />
          </button>

          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <img src="/pinnacle_logo.png" alt="Occult King" className="h-8 w-8 rounded" />
            <span className="font-semibold text-slate-800 text-sm">Occult King</span>
          </div>

          {/* Login / Dashboard Button */}
          {user ? (
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 transition-colors"
              data-testid="mobile-dashboard-btn"
            >
              <LayoutDashboard size={18} className="text-white" />
              <span className="text-white text-xs font-semibold">Dashboard</span>
            </button>
          ) : (
            <button 
              onClick={() => navigate('/login')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 transition-colors"
              data-testid="mobile-login-btn"
            >
              <LogIn size={18} className="text-white" />
              <span className="text-white text-xs font-semibold">Login</span>
            </button>
          )}
        </div>
      </header>
      )}

      {/* Side Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Side Menu Drawer */}
      <div className={`md:hidden fixed top-0 left-0 bottom-0 w-[280px] z-[70] bg-white shadow-2xl transform transition-transform duration-300 ease-out safe-area-left ${
        isMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Menu Header */}
        <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img src="/pinnacle_logo.png" alt="Occult King" className="h-10 w-10 rounded" />
              <div>
                <h2 className="font-bold text-slate-800">Occult King</h2>
                <p className="text-xs text-slate-500">Scientific Vastu Solutions</p>
              </div>
            </div>
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200"
            >
              <X size={20} className="text-slate-600" />
            </button>
          </div>

          {/* User Info */}
          {user ? (
            <div className="bg-slate-100 rounded-xl p-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-800 font-medium truncate">{user.name || 'User'}</p>
                  <p className="text-slate-500 text-xs truncate">{user.email}</p>
                </div>
              </div>
            </div>
          ) : (
            <button 
              onClick={() => handleNavClick('/login')}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white font-semibold"
            >
              Login / Sign Up
            </button>
          )}
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto py-4 bg-white">
          <div className="px-3 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  item.highlight 
                    ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200' 
                    : item.premium
                    ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200'
                    : 'hover:bg-slate-50'
                }`}
              >
                <item.icon size={20} className={
                  item.highlight ? 'text-amber-600' : 
                  item.premium ? 'text-purple-600' : 
                  'text-slate-500'
                } />
                <span className={`flex-1 text-left ${
                  item.highlight ? 'text-amber-700 font-medium' : 
                  item.premium ? 'text-purple-700 font-medium' :
                  'text-slate-700'
                }`}>
                  {item.label}
                </span>
                {item.premium && (
                  <span className="px-2 py-0.5 bg-purple-500 text-white text-[10px] rounded-full font-medium">
                    PRO
                  </span>
                )}
                <ChevronRight size={16} className="text-slate-300" />
              </button>
            ))}
          </div>

          {/* Notification Toggle */}
          <div className="px-3 mt-4">
            <div className="border-t border-slate-200 pt-4">
              <button
                onClick={enableNotifications}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50"
              >
                <Bell size={20} className={notificationsEnabled ? 'text-amber-600 fill-current' : 'text-slate-500'} />
                <span className="flex-1 text-left text-slate-700">
                  {notificationsEnabled ? 'Notifications On' : 'Enable Notifications'}
                </span>
                <div className={`w-10 h-6 rounded-full transition-colors ${
                  notificationsEnabled ? 'bg-amber-500' : 'bg-slate-300'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform mt-0.5 ${
                    notificationsEnabled ? 'translate-x-4.5 ml-4' : 'translate-x-0.5 ml-0.5'
                  }`} />
                </div>
              </button>
            </div>
          </div>

          {/* Install App Prompt - Only show if not already installed as PWA */}
          {!isPWA && (
            <div className="px-3 mt-4">
              <button
                onClick={() => handleNavClick('/install')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200"
              >
                <Download size={20} className="text-indigo-600" />
                <span className="flex-1 text-left text-indigo-700 font-medium">Install App</span>
                <ChevronRight size={16} className="text-indigo-400" />
              </button>
            </div>
          )}
        </div>

        {/* Logout Button */}
        {user && (
          <div className="p-4 border-t border-slate-200 bg-white safe-area-bottom">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 transition-colors border border-red-200"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Main Content - Add padding for header and bottom nav */}
      <main className={`flex-1 md:pt-0 ${hideMobileHeader ? 'pt-0' : 'pt-[60px]'} ${hideBottomNav ? 'pb-0' : 'pb-[70px]'} md:pb-0`}>
        {children}
      </main>

      {/* Bottom Navigation - hidden on Remedies product pages for a focused buy flow */}
      {!hideBottomNav && (
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.1)] border-t border-slate-200 safe-area-bottom" data-testid="bottom-nav">
        <div className="flex items-center justify-around px-2 py-2">
          {bottomNavItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.path)}
                className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-amber-50' 
                    : 'hover:bg-slate-50'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon 
                  size={22} 
                  className={`transition-colors ${
                    isActive ? 'text-amber-600' : 'text-slate-400'
                  }`}
                  fill={isActive ? 'currentColor' : 'none'}
                />
                <span className={`text-[10px] mt-1 font-medium ${
                  isActive ? 'text-amber-600' : 'text-slate-400'
                }`}>
                  {item.label}
                </span>
                {/* Active indicator dot */}
                {isActive && (
                  <div className="absolute -bottom-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );
}

// Helper function for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default MobileAppShell;
